import moment from "moment";
import { buildStageIndex } from "@/modules/fxCrm/helpers/interactionCard";
import { traducirAlertaDeFila } from "@/modules/fxCrm/helpers/assignmentAlertsApi";

/**
 * Etapas de cada estado, aplanadas y en orden (fase.priority → etapa.priority).
 * Se calcula UNA vez por catálogo y no por tarjeta: un tablero puede tener
 * cientos de interacciones y todas consultan este mismo índice.
 */
export const kanbanStageIndex = (state) =>
  buildStageIndex(
    state.kanbanPhases?.data?.results,
    state.kanbanStages?.data?.results
  );

export const getUsers = (state) => {
  if (state.users_pbx.data) {
    const baseUrl = process.env.VUE_APP_BASE_API;
    return state.users_pbx.data.results.map((element) => {
      return {
        ...element,
        name: `${element.first_name} ${element.last_name}`,
        avatar: element.avatar_url ? `${baseUrl}${element.avatar_url}` : null,
      };
    });
  }
  return [];
};

export const getTransactions = (state) => {
  return state.transactions.results.map((element) => {
    return {
      ...element,
      date: `${moment(element.timestamp).format("DD/MM/YYYY HH:mm")}`,
    };
  });
};

// --- Alertas de asignación (`.claude/alertas_crm/`) ---

/** Las recibidas, recorridas una vez por cálculo. */
const itemsRecibidos = (state) =>
  state.assignmentAlerts.recibidas.flatMap((alerta) => alerta.items);

/**
 * El número del badge de asignaciones: interacciones aún sin ver. El del
 * servidor si lo hay (la lista viene paginada); si todavía no llegó (antes de
 * la primera carga), se cuenta la lista.
 */
export const unseenAssignmentsCount = (state) =>
  state.assignmentAlerts.unseenCount ??
  itemsRecibidos(state).filter((item) => item.estado === "nueva").length;

/**
 * `id de gestión → item sin ver` (la alerta más nueva, si está en varias).
 * Las vistas preguntan aquí si una fila o tarjeta va resaltada. El valor es
 * el MISMO objeto del state, así una tarjeta que no cambió recibe la misma
 * referencia y no se vuelve a pintar (tableros con cientos de tarjetas).
 */
export const unseenAssignmentById = (state) => {
  const mapa = {};
  itemsRecibidos(state).forEach((item) => {
    if (item.estado === "nueva" && !(item.id in mapa)) mapa[item.id] = item;
  });
  return mapa;
};

/**
 * El aviso que pinta una tarjeta o fila del gestor, o `null`. Es la ÚNICA
 * pregunta que hacen las vistas:
 *   1. el store: lo que cargó la campana o llegó en vivo por el socket (con su
 *      alerta completa);
 *   2. si no, el `assignment_alert` que trae la propia fila: el backend lo
 *      calcula por tarjeta, así que no depende de qué alertas cargó la
 *      campana (limitada a 50). Se apaga si ya se marcó visto en esta sesión
 *      (`filasVistas`) o si el store sabe que ese ítem ya no está sin ver.
 *
 * Uso: `assignmentAlertFor(fila)`.
 */
export const assignmentAlertFor = (state, getters) => (fila) => {
  if (!fila) return null;
  const enStore = getters.unseenAssignmentById[fila.id];
  if (enStore) return enStore;
  if (!fila.assignment_alert || state.assignmentAlerts.filasVistas[fila.id]) return null;
  const itemId = fila.assignment_alert.item_id;
  const yaVisto = itemsRecibidos(state).some(
    (item) => item.itemId === itemId && item.estado !== "nueva"
  );
  return yaVisto ? null : traducirAlertaDeFila(fila);
};

/** `alertaId → alerta`, de recibidas y enviadas (el detalle necesita remitente y grupo). */
export const assignmentAlertById = (state) => {
  const mapa = {};
  [...state.assignmentAlerts.recibidas, ...state.assignmentAlerts.enviadas].forEach(
    (alerta) => {
      mapa[alerta.id] = alerta;
    }
  );
  return mapa;
};

// ---------------------------------------------------------------------------
// Campana del CRM (FC-743) — `.claude/crm_notificaciones/`
// ---------------------------------------------------------------------------

/**
 * El número del badge. **Suma los dos mundos** (decisión del usuario,
 * 2026-10-26): los avisos de «Para hacer» del nuevo endpoint y las asignaciones
 * sin ver, que son la sección que se conserva y que NO viaja en `to_do/`.
 */
export const bellBadgeCount = (state, getters) =>
  state.bell.toDo.total + (getters.unseenAssignmentsCount || 0);

/**
 * Si el badge va en rojo. OJO: **no** se calcula sobre la suma de arriba. El
 * rojo lo decide la urgencia de «Para hacer»; las asignaciones no tienen
 * urgencia, así que un badge alto puede seguir siendo neutro.
 */
export const bellHasUrgent = (state) => state.bell.toDo.urgent > 0;

/** Contador de cada pestaña. El de Seguimiento suma 1 si existe el bloque de vencidas (FC-749). */
export const bellTabCounts = (state) => ({
  toDo: state.bell.toDo.total,
  news: state.bell.news.total,
  followUp:
    state.bell.followUp.stalled.total +
    (state.bell.followUp.overdue_tasks.total > 0 ? 1 : 0),
});

/**
 * El subtítulo de la cabecera: «N para hacer · M urgentes · K novedades».
 *
 * El tramo de urgentes solo aparece si los hay, y «novedad» va en singular con
 * una sola: las dos cosas, tal cual las pinta el mockup.
 */
export const bellSubtitle = (state) => {
  const { total: porHacer, urgent } = state.bell.toDo;
  const novedades = state.bell.news.total;
  const partes = [`${porHacer} para hacer`];
  if (urgent > 0) partes.push(`${urgent} urgentes`);
  partes.push(`${novedades} ${novedades === 1 ? "novedad" : "novedades"}`);
  return partes.join(" · ");
};

/** El texto emergente del botón de la campana (FC-745). */
export const bellTooltip = (state) => {
  const { total, urgent } = state.bell.toDo;
  if (!total) return "Nada pendiente";
  return urgent > 0 ? `${total} para hacer (${urgent} urgentes)` : `${total} para hacer`;
};
